import { createElement, forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import {
  getBaseHeadingSizeClassName,
  getBaseTypographyToneClassName,
  resolveBaseAppearance,
} from './theme';
import type { BaseHeadingLevel, BaseHeadingProps } from './types';
import { cn } from './utils';

function isHeadingLevel(value: unknown): value is BaseHeadingLevel {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 6
  );
}

export const BaseHeading = forwardRef<HTMLHeadingElement, BaseHeadingProps>(
  function BaseHeading(
    {
      level = 2,
      children,
      size = 'lg',
      tone = 'default',
      appearance,
      className,
      classNames,
      style,
      ...headingProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);

    if (!isHeadingLevel(level)) {
      throw new RangeError('BaseHeading level must be an integer from 1 to 6.');
    }

    return createElement(
      `h${level}`,
      {
        ...componentAttributes,
        ...headingProps,
        ref,
        'data-monkeys-component': 'heading',
        'data-slot': 'root',
        'data-level': level,
        'data-size': size,
        'data-tone': tone,
        className: cn(
          theme.slots.headingRoot,
          getBaseHeadingSizeClassName(theme, size),
          getBaseTypographyToneClassName(theme, tone),
          classNames?.root,
          className,
        ),
        style: {
          ...theme.styles.heading,
          ...style,
        },
      },
      children,
    );
  },
);
